import styled from '@emotion/styled';
import { AnimatePresence, motion } from 'motion/react';
import { useState, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import type { ReviewImage } from '..';

interface DragInfo {
  offset: {
    x: number;
    y: number;
  };
  velocity: {
    x: number;
    y: number;
  };
}

interface ImageGalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: ReviewImage[];
}

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 999998;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContainer = styled(motion.div)`
  position: relative;
  width: 90vw;
  max-width: 800px;
  height: 90vh;
  max-height: 600px;
  background: white;
  border-radius: 20px;
  z-index: 999999;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const MobileDrawer = styled(motion.div)`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: 85vh;
  background: white;
  border-radius: 20px 20px 0 0;
  z-index: 999999;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  touch-action: none;
`;

const Header = styled.div`
  padding: 24px;
  position: relative;
  flex-shrink: 0;
`;

const MobileHeader = styled.div`
  padding: 20px;
  text-align: center;
  position: relative;
  flex-shrink: 0;

  &::before {
    content: '';
    position: absolute;
    top: 8px;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 4px;
    background: #ddd;
    border-radius: 2px;
  }
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 500;
  color: #2e2e2e;
  margin: 0;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const MobileTitle = styled.h2`
  font-size: 16px;
  font-weight: 500;
  color: #2e2e2e;
  margin: 0;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 50%;
  right: 24px;
  transform: translateY(-50%);
  background: none;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition:
    background-color 0.2s,
    color 0.2s;

  &:hover {
    background-color: #f5f5f5;
    color: #333;
  }
`;

const MobileCloseButton = styled.button`
  position: absolute;
  top: 50%;
  right: 20px;
  transform: translateY(-50%);
  background: none;
  border: none;
  font-size: 20px;
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  transition:
    background-color 0.2s,
    color 0.2s;

  &:hover {
    background-color: #f5f5f5;
    color: #333;
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 24px 24px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const MobileContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 20px 20px;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  padding-top: 24px;
`;

const MobileImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding-top: 20px;
`;

const ImageContainer = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 24px;
  overflow: hidden;
  cursor: pointer;
  transition:
    transform 0.2s,
    box-shadow 0.2s;
  background: #f5f5f5;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
`;

const MobileImageContainer = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  background: #f5f5f5;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const LoadMoreButton = styled.button`
  width: 100%;
  padding: 16px;
  margin-top: 24px;
  background: #f8f8f8;
  border: 2px solid #e5e5e5;
  border-radius: 12px;
  color: #333;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Plus Jakarta Sans', sans-serif;

  &:hover {
    background: #f0f0f0;
    border-color: #d0d0d0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MobileLoadMoreButton = styled.button`
  width: 100%;
  padding: 14px;
  margin-top: 20px;
  background: #f8f8f8;
  border: 2px solid #e5e5e5;
  border-radius: 8px;
  color: #333;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Plus Jakarta Sans', sans-serif;

  &:hover {
    background: #f0f0f0;
    border-color: #d0d0d0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #666;
  text-align: center;
`;

const EmptyStateText = styled.p`
  font-size: 16px;
  margin: 0;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const INITIAL_VISIBLE_COUNT = 6;
const LOAD_MORE_COUNT = 6;

export const ImageGalleryModal = ({ isOpen, onClose, images }: ImageGalleryModalProps) => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);

  // Reset visible count when modal opens
  useEffect(() => {
    if (isOpen) {
      setVisibleCount(INITIAL_VISIBLE_COUNT);
    }
  }, [isOpen]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleLoadMore = () => {
    setVisibleCount((prev) => Math.min(prev + LOAD_MORE_COUNT, images.length));
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: DragInfo) => {
    if (info.offset.y > 200 || info.velocity.y > 200) {
      onClose();
    }
  };

  const visibleImages = images.slice(0, visibleCount);
  const hasMoreImages = visibleCount < images.length;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {isMobile ? (
            <>
              <Overlay
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={handleOverlayClick}
              />
              <MobileDrawer
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 500 }}
                drag="y"
                dragConstraints={{ top: 0, bottom: 0 }}
                onDragEnd={handleDragEnd}
              >
                <MobileHeader>
                  <MobileTitle>Customer Images</MobileTitle>
                  <MobileCloseButton onClick={onClose} aria-label="Close modal">
                    ×
                  </MobileCloseButton>
                </MobileHeader>

                <MobileContent>
                  {images.length === 0 ? (
                    <EmptyState>
                      <EmptyStateText>No customer images yet</EmptyStateText>
                    </EmptyState>
                  ) : (
                    <>
                      <MobileImageGrid>
                        {visibleImages.map((image, index) => (
                          <MobileImageContainer key={image.id || index}>
                            <Image
                              src={image.url}
                              alt={image.alt || `Customer image ${index + 1}`}
                              loading={index < 6 ? 'eager' : 'lazy'}
                              onError={(e) => {
                                console.error(`Failed to load image: ${image.url}`);
                              }}
                            />
                          </MobileImageContainer>
                        ))}
                      </MobileImageGrid>

                      {hasMoreImages && (
                        <MobileLoadMoreButton onClick={handleLoadMore}>
                          Load {Math.min(LOAD_MORE_COUNT, images.length - visibleCount)} more images
                        </MobileLoadMoreButton>
                      )}
                    </>
                  )}
                </MobileContent>
              </MobileDrawer>
            </>
          ) : (
            <Overlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={handleOverlayClick}
            >
              <ModalContainer
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ type: 'spring', damping: 25, stiffness: 500 }}
              >
                <Header>
                  <Title>Customer Images</Title>
                  <CloseButton onClick={onClose} aria-label="Close modal">
                    ×
                  </CloseButton>
                </Header>

                <Content>
                  {images.length === 0 ? (
                    <EmptyState>
                      <EmptyStateText>No customer images yet</EmptyStateText>
                    </EmptyState>
                  ) : (
                    <>
                      <ImageGrid>
                        {visibleImages.map((image, index) => (
                          <ImageContainer key={image.id || index}>
                            <Image
                              src={image.url}
                              alt={image.alt || `Customer image ${index + 1}`}
                              loading={index < 6 ? 'eager' : 'lazy'}
                              onError={(e) => {
                                console.error(`Failed to load image: ${image.url}`);
                              }}
                            />
                          </ImageContainer>
                        ))}
                      </ImageGrid>

                      {hasMoreImages && (
                        <LoadMoreButton onClick={handleLoadMore}>
                          Load {Math.min(LOAD_MORE_COUNT, images.length - visibleCount)} more images
                        </LoadMoreButton>
                      )}
                    </>
                  )}
                </Content>
              </ModalContainer>
            </Overlay>
          )}
        </>
      )}
    </AnimatePresence>
  );
};

export default ImageGalleryModal;
